/* DiaryGuide — patient-friendly tour of all Хранителен дневник modules.
 *
 *  Дизайн: warm, разговорен tone. Без технически термини, без научни формули,
 *  без вътрешни file имена. Фокус: какво виждам, какво да правя, как ми помага.
 *
 *  Регулаторно: всички медицински фрази са с patient-safe рамка
 *  ("теми за обсъждане с лекар", "образователен инструмент").
 */
import React, { useEffect, useState } from "react";
import {
  Compass, X, ChevronRight, ChevronLeft, Sparkles, Utensils, Activity, ChefHat,
  Bell, BookOpen, BarChart3, AlertTriangle, CalendarDays, ClipboardList, LineChart,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

const STORAGE_KEY = "diary_guide_seen";

const MODULES = [
  {
    id: "plate",
    icon: Utensils,
    color: "#10B981",
    name_bg: "Симулатор на чинията",
    name_en: "Plate Simulator",
    aiPowered: true,
    purpose_bg: "Изпробвайте състава на едно ястие, преди да го изядете. Виждате веднага дали съчетанието е уравновесено.",
    purpose_en: "Test a meal's composition before you eat it. See instantly whether the combination is balanced.",
    benefit_bg: "Помага Ви да избирате между две-три възможности и да усетите как ще се отрази обядът.",
    benefit_en: "Helps you choose between 2-3 options and feel how the meal will affect you.",
    action_bg: "Натиснете „Добавяне на храна“ → сглобете чиния → вижте цвета и съветите.",
    action_en: "Tap \"Add food\" → assemble plate → see colors and tips.",
    jumpId: "diary-section-plate",
  },
  {
    id: "twin",
    icon: Activity,
    color: "#8B5CF6",
    name_bg: "Вашият дигитален близнак",
    name_en: "Your digital twin",
    aiPowered: true,
    purpose_bg: "Пресмята как избран хранителен подход или промяна в начина на живот ще се отрази на Вас през следващите 30 дни — на основата на Вашите данни.",
    purpose_en: "Simulates how a chosen nutritional approach or lifestyle change will affect you over the next 30 days — based on your data.",
    benefit_bg: "Не е магия — стъпва на доказана наука и на изкуствен интелект, за да направи предвиждане точно за Вашия профил.",
    benefit_en: "Not magic — uses proven science and AI to make a projection specific to your profile.",
    action_bg: "Изберете въздействие (средиземноморско хранене · 16:8 · движение) → вижте очакваните промени.",
    action_en: "Pick an intervention (Mediterranean · 16:8 · exercise) → see expected changes.",
    jumpId: "diary-section-twin",
  },
  {
    id: "intelligence",
    icon: Sparkles,
    color: "#F59E0B",
    name_bg: "Умно хранене",
    name_en: "Smart Nutrition",
    aiPowered: true,
    purpose_bg: "Разглежда какво сте яли през деня и предлага малки, лесни поправки — без забрани и без сложни режими.",
    purpose_en: "Analyzes what you ate today and suggests small, easy corrections — no bans, no complicated diets.",
    benefit_bg: "Ако Ви липсва нещо (например белтък или влакнини), показва какво да добавите в следващото ястие.",
    benefit_en: "If something's missing (e.g. protein or fiber), shows what you can add to your next meal.",
    action_bg: "Просто добавяйте ястия в дневника — съветите се обновяват сами.",
    action_en: "Just log meals in the diary — suggestions update automatically.",
    jumpId: "diary-section-intelligence",
  },
  {
    id: "daily_recommendation",
    icon: Bell,
    color: "#06B6D4",
    name_bg: "Ежедневна препоръка",
    name_en: "Daily Recommendation",
    purpose_bg: "Получавате кратко напомняне в подходящия час: сутрин — закуска, около обед — обяд, вечер — лека вечеря.",
    purpose_en: "You get a small reminder at the right time: morning — breakfast, around noon — lunch, evening — light dinner.",
    benefit_bg: "Помага да не пропускате храненията и да не отлагате тежко ядене за късно вечер.",
    benefit_en: "Helps you not skip meals and not eat heavy late at night.",
    action_bg: "Картата се появява сама с конкретна идея за следващото ястие.",
    action_en: "The card appears automatically with a specific idea for your next meal.",
    jumpId: null,  // visible automatically based on hour
  },
  {
    id: "meal_log",
    icon: BookOpen,
    color: "#EC4899",
    name_bg: "Хранителен дневник",
    name_en: "Meal Log",
    purpose_bg: "Тук записвате всичко, което ядете. Може с текст, със снимка на чиния, със снимка на рецепта или с глас 🎤.",
    purpose_en: "Here you log everything you eat. By text, plate photo, recipe photo, or voice 🎤.",
    benefit_bg: "Калориите и градивните вещества се изчисляват сами — не пресмятате нищо на ръка.",
    benefit_en: "Calories and macros calculated automatically — no manual math.",
    action_bg: "Започнете от закуската. Гласовият запис е най-бързият начин: „две яйца и един домат“.",
    action_en: "Start with breakfast. Voice is the fastest: \"2 eggs and a tomato\".",
    jumpId: "diary-section-meallog",
  },
  {
    id: "patterns",
    icon: BarChart3,
    color: "#A855F7",
    name_bg: "Хранителни модели",
    name_en: "Dietary Patterns",
    purpose_bg: "Показва повторенията в храненето Ви през последните седмици — кои храни най-често и в кои часове.",
    purpose_en: "Shows patterns in your eating over recent weeks — which foods most often, at which hours.",
    benefit_bg: "Помага да забележите повтарящи се навици, които можете да поправите (например твърде късни вечери).",
    benefit_en: "Helps you notice recurring habits to adjust (e.g. late dinners).",
    action_bg: "Просто разгледайте графиките — те се обновяват сами.",
    action_en: "Just review the charts — they update automatically.",
    jumpId: "diary-section-patterns",
  },
  {
    id: "deficiency",
    icon: AlertTriangle,
    color: "#EF4444",
    name_bg: "Анализ на дефицити",
    name_en: "Nutrient Deficiency",
    purpose_bg: "Откриваме възможни недостатъци (витамин D, желязо, B12 и други) според това, което ядете.",
    purpose_en: "We detect potential shortfalls (vitamin D, iron, B12, etc.) based on what you eat.",
    benefit_bg: "Помага да обсъдите с лекаря дали имате нужда от изследвания или от добавки.",
    benefit_en: "Helps you discuss with a doctor whether you need testing or supplements.",
    action_bg: "Разчитането се обновява само — проверете седмичния си резултат.",
    action_en: "Analysis updates automatically — check your weekly result.",
    jumpId: "diary-section-deficiency",
  },
  {
    id: "ai_planner",
    icon: ChefHat,
    color: "#F97316",
    name_bg: "Съставяне на хранителен план",
    name_en: "AI Meal Planner",
    aiPowered: true,
    purpose_bg: "Кажете ни целта си, любимите храни и с колко време разполагате — изкуственият интелект съставя план за 3 до 7 дни, съобразен с Вас.",
    purpose_en: "Tell us your goal, favorite foods and time — AI generates a 3-7 day plan tailored to you.",
    benefit_bg: "Спестявате часове в обмисляне „какво да ям утре“ — планът е готов, а и списъкът за пазаруване.",
    benefit_en: "Saves you hours of \"what should I eat tomorrow\" — the plan is ready and so is the shopping list.",
    action_bg: "Натиснете „Съставяне на план“ → отговорете на три-четири въпроса → получавате плана.",
    action_en: "Tap \"Generate plan\" → answer 3-4 questions → get the plan.",
    jumpId: "diary-section-planner",
  },
  {
    id: "plan_review",
    icon: ClipboardList,
    color: "#6366F1",
    name_bg: "Преглед на хранителен режим",
    name_en: "Meal Plan Review",
    aiPowered: true,
    purpose_bg: "Получавате честна оценка на сегашния си режим — уравновесен ли е, какво да добавите и какво да намалите.",
    purpose_en: "Get an honest review of your current pattern — is it balanced, what to add, what to reduce.",
    benefit_bg: "Не дава забрани — вниманието е върху „какво още да добавя“, не „какво да изключа“.",
    benefit_en: "No bans — focus on \"what else to add\", not \"what to exclude\".",
    action_bg: "Запишете поне пет дни → отворете прегледа → вижте конкретните препоръки.",
    action_en: "Log at least 5 days → open the review → see concrete recommendations.",
    jumpId: "diary-section-review",
  },
  {
    id: "weekly",
    icon: LineChart,
    color: "#22C55E",
    name_bg: "Седмичен преглед",
    name_en: "Weekly Review",
    purpose_bg: "Графика на последните седем дни — какво сте научили за себе си и какъв напредък сте постигнали.",
    purpose_en: "Chart of last 7 days — what you learned about yourself and what progress you made.",
    benefit_bg: "Виждате ясно дали сте в добра посока — без да се ровите в калории и таблици.",
    benefit_en: "Clearly see if you're on track — without digging through calories and tables.",
    action_bg: "Прегледайте го в неделя вечер — отнема две минути и помага да започнете следващата седмица подготвени.",
    action_en: "Review Sunday evening — takes 2 min, helps start next week planned.",
    jumpId: "diary-section-weekly",
  },
];

export default function DiaryGuide({ autoOpenFirstTime = true }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Auto-open on first visit
  useEffect(() => {
    if (!autoOpenFirstTime) return;
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        const t = setTimeout(() => setOpen(true), 1200);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, [autoOpenFirstTime]);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  };

  const currentModule = MODULES[step];
  const Icon = currentModule.icon;

  const goToModule = () => {
    if (currentModule.jumpId) {
      const el = document.getElementById(currentModule.jumpId);
      if (el) {
        close();
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          el.classList.add("ring-2", "ring-violet-400", "ring-offset-2", "transition-all");
          setTimeout(() => el.classList.remove("ring-2", "ring-violet-400", "ring-offset-2"), 2000);
        }, 200);
      }
    }
  };

  return (
    <>
      {/* iter540 — Floating "Ръководство" trigger ПРЕМАХНАТ по user request
          ("стои много грозно в хранителния дневник"). Tour modal остава —
          auto-отваря се на първо посещение (STORAGE_KEY="diary_guide_seen"),
          но няма постоянна floating икона. Достъп до tour може да се
          реактивира при нужда чрез нов entry point. */}

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
          onClick={close}
          data-testid="diary-guide-modal"
        >
          <div
            className={`w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden ${
              isDark ? "bg-[hsl(223,47%,10%)] border border-[hsl(223,47%,20%)]" : "bg-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="px-5 py-4 flex items-center justify-between gap-3"
              style={{
                background: `linear-gradient(135deg, ${currentModule.color} 0%, ${currentModule.color}dd 100%)`,
              }}
            >
              <div className="flex items-center gap-3 text-white">
                <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider opacity-90 font-semibold">
                    {bg ? `Стъпка ${step + 1} от ${MODULES.length}` : `Step ${step + 1} of ${MODULES.length}`}
                  </div>
                  <h3 className="text-base font-bold leading-tight line-clamp-2">
                    {bg ? currentModule.name_bg : currentModule.name_en}
                  </h3>
                </div>
              </div>
              <button
                onClick={close}
                className="p-1.5 rounded-lg hover:bg-white/20 text-white"
                aria-label={bg ? "Затваряне" : "Close"}
                data-testid="diary-guide-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-3" data-testid={`diary-guide-step-${currentModule.id}`}>
              {currentModule.aiPowered && (
                <div
                  className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${
                    isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-50 text-violet-700"
                  }`}
                >
                  <Sparkles className="w-3 h-3" />
                  {bg ? "С помощта на изкуствен интелект" : "Powered by AI"}
                </div>
              )}

              <div>
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                  {bg ? "Какво е това" : "What it is"}
                </p>
                <p className={`text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  {bg ? currentModule.purpose_bg : currentModule.purpose_en}
                </p>
              </div>

              <div>
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-cyan-300" : "text-cyan-700"}`}>
                  {bg ? "С какво Ви помага" : "How it helps you"}
                </p>
                <p className={`text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  {bg ? currentModule.benefit_bg : currentModule.benefit_en}
                </p>
              </div>

              <div>
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  {bg ? "Какво да направите" : "What to do"}
                </p>
                <p className={`text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  {bg ? currentModule.action_bg : currentModule.action_en}
                </p>
              </div>

              {currentModule.jumpId && (
                <button
                  onClick={goToModule}
                  className={`w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold transition-colors ${
                    isDark
                      ? "bg-[hsl(223,47%,15%)] text-white hover:bg-[hsl(223,47%,20%)]"
                      : "bg-slate-100 text-slate-800 hover:bg-slate-200"
                  }`}
                  data-testid="diary-guide-jump-btn"
                >
                  {bg ? "Покажете ми този раздел" : "Take me to this module"}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Footer + Navigation */}
            <div
              className={`px-5 py-3 border-t flex items-center justify-between gap-3 ${
                isDark ? "border-[hsl(223,47%,20%)] bg-[hsl(223,47%,8%)]" : "border-gray-100 bg-slate-50"
              }`}
            >
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 ${
                  step === 0
                    ? "opacity-30 cursor-not-allowed"
                    : (isDark ? "text-gray-300 hover:bg-white/5" : "text-gray-600 hover:bg-gray-100")
                }`}
                data-testid="diary-guide-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                {bg ? "Назад" : "Back"}
              </button>

              {/* Dots */}
              <div className="flex gap-1.5">
                {MODULES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setStep(i)}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      i === step
                        ? "bg-violet-500 scale-150"
                        : (isDark ? "bg-gray-700" : "bg-gray-300")
                    }`}
                    aria-label={`${bg ? "Стъпка" : "Step"} ${i + 1}`}
                  />
                ))}
              </div>

              {step === MODULES.length - 1 ? (
                <button
                  onClick={close}
                  className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-md"
                  data-testid="diary-guide-finish"
                >
                  {bg ? "Разбрах" : "I'm ready"}
                </button>
              ) : (
                <button
                  onClick={() => setStep((s) => Math.min(MODULES.length - 1, s + 1))}
                  className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 ${
                    isDark ? "bg-violet-500/20 text-violet-200 hover:bg-violet-500/30" : "bg-violet-100 text-violet-700 hover:bg-violet-200"
                  }`}
                  data-testid="diary-guide-next"
                >
                  {bg ? "Напред" : "Next"}
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Disclaimer */}
            <div
              className={`px-5 py-2.5 text-[10px] leading-relaxed text-center ${
                isDark ? "text-gray-500 bg-[hsl(223,47%,6%)]" : "text-gray-400 bg-white border-t border-gray-100"
              }`}
            >
              {bg
                ? "Образователен инструмент. Не замества лекарска консултация."
                : "Educational tool. Does not replace medical consultation."}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
